/**
 * PickExecutor - Wraps VTK CellPicker for picking.
 *
 * Responsibilities:
 * - Execute VTK cell picking against a managed actor list
 * - Convert VTK selection to a PickHit (world position + actor)
 * - Provide direct addActors / removeActors for pick list management
 * - Handle errors gracefully
 */

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkCellPicker from '@kitware/vtk.js/Rendering/Core/CellPicker';
import vtkProp3D from '@kitware/vtk.js/Rendering/Core/Prop3D';

import type { IViewport } from '../viewports';
import type { InteractionEvent } from './types';
import { Vec3 } from '../domain';

export interface PickHit {
    /** World coordinates of the pick point */
    worldPosition: Vec3;
    /** The VTK actor that was hit */
    actor: vtkActor;
    /** Cell ID on the hit actor */
    cellId: number;
}

export class PickExecutor {
    private picker: vtkCellPicker;

    constructor(private viewport: IViewport) {
        this.picker = vtkCellPicker.newInstance();
        this.picker.setTolerance(0.005);
        this.picker.setPickFromList(true);
        this.picker.initializePickList();
    }

    // ── Pick list management ──

    addProps(actors: vtkProp3D[]): void {
        for (const actor of actors) {
            this.picker.addPickList(actor);
        }
    }

    removeProps(actors: vtkProp3D[]): void {
        for (const actor of actors) {
            this.picker.deletePickList(actor);
        }
    }

    // ── Picking ──

    /**
     * Execute pick at the event position.
     * Returns a PickHit if an actor was hit, null otherwise.
     */
    pick(event: InteractionEvent): PickHit | null {
        try {
            const canvas = this.viewport.getCanvas();
            if (!canvas) return null;

            const screenPos = this.getScreenEventPosition(event);
            const point: [number, number, number] = [screenPos.x, screenPos.y, 0.0];

            const renderers = this.viewport.getAllRenderer();
            if (renderers.length === 0) return null;

            // Try picking in each renderer, keep the closest hit
            let bestHit: PickHit | null = null;
            let bestDistance = Infinity;

            for (const renderer of renderers) {
                this.picker.pick(point, renderer);

                const cellId = this.picker.getCellId();
                if (cellId < 0) continue;

                const pickedActors = this.picker.getActors();
                const actorList = pickedActors
                    ? Array.isArray(pickedActors)
                        ? pickedActors
                        : Array.from(pickedActors)
                    : [];
                if (actorList.length === 0) continue;

                const actor = actorList[0] as vtkActor;
                if (!actor) continue;

                const worldPosition = this.picker.getPickPosition() as Vec3;

                // Use pick distance to keep closest hit across renderers
                const pickDistance = worldPosition[0] ** 2 + worldPosition[1] ** 2 + worldPosition[2] ** 2;
                if (pickDistance >= bestDistance) continue;

                bestDistance = pickDistance;
                bestHit = { worldPosition, actor, cellId };
            }

            return bestHit;
        } catch (error) {
            console.error('[PickExecutor] Pick operation failed:', error);
            return null;
        }
    }

    dispose(): void {
        // No external registrations to clean up
    }

    // ── Internals ──

    /**
     * Convert event position to VTK screen coordinates.
     */
    private getScreenEventPosition(event: InteractionEvent): { x: number; y: number; z: number } {
        const canvas = this.viewport.getCanvas();
        if (!canvas) {
            return { x: 0, y: 0, z: 0 };
        }

        const canvasBounds = canvas.getBoundingClientRect();
        const openGLRenderWindow = this.viewport.getOpenGLRenderWindow();
        const [canvasWidth, canvasHeight] = openGLRenderWindow.getSize();

        const scaleX = canvasWidth / canvasBounds.width;
        const scaleY = canvasHeight / canvasBounds.height;

        // Calculate position relative to canvas
        const canvasRelativeX = event.currentPoints.client[0] - canvasBounds.left;
        const canvasRelativeY = event.currentPoints.client[1] - canvasBounds.top;

        return {
            x: Math.round(scaleX * canvasRelativeX),
            y: Math.round(scaleY * (canvasBounds.height - canvasRelativeY)),
            z: 0,
        };
    }
}
